'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Send,
  AlertCircle,
  BookOpen,
  Link as LinkIcon,
  MessageSquare,
  CheckCircle2,
  Loader2, Info
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useGame } from '@/context/GameContext';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type FeedbackType = 'REPORT' | 'SUGGESTION';

export interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: FeedbackType;
  initialWord?: string;
  wordId?: number;
}

const REPORT_REASONS = [
  { id: 'MISWRITTEN', label: 'E shkruar gabim' },
  { id: 'WRONG_DEFINITION', label: 'Kuptimi i gabuar' },
  { id: 'MISSING_DEFINITION', label: 'Plotëso kuptimin' },
  { id: 'NOT_ALBANIAN', label: 'Nuk është fjalë shqipe' },
  { id: 'OFFENSIVE', label: 'Fjalë fyese' },
  { id: 'OTHER', label: 'Tjetër' },
];

const SUGGESTION_SOURCES = [
  { id: 'FJALORTHI', label: 'Fjalorthi' },
  { id: 'FJALORI_ONLINE', label: 'Fjalori.Online' },
  { id: 'FJALE_AL', label: 'Fjale.al' },
  { id: 'LITERATURE', label: 'Libra / Letërsi' },
  { id: 'POPULAR', label: 'Gjuhë Popullore' },
  { id: 'OTHER', label: 'Tjetër...' },
];

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  type: initialType,
  initialWord = '',
  wordId
}) => {
  const { fetchWithId } = useGame();
  const [type, setType] = useState<FeedbackType>(initialType);
  const [word, setWord] = useState(initialWord);
  const [definition, setDefinition] = useState('');
  const [sources, setSources] = useState('');
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New states for real-time word checking
  const [isCheckingWord, setIsCheckingWord] = useState(false);
  const [wordStatus, setWordStatus] = useState<'IDLE' | 'NEW' | 'EXISTS_NO_DEF' | 'EXISTS_WITH_DEF'>('IDLE');

  const isValid = React.useMemo(() => {
    if (!word.trim()) return false;
    if (type === 'REPORT') {
      if (!reason) return false;
      if ((reason === 'MISSING_DEFINITION' || reason === 'WRONG_DEFINITION') && !definition.trim()) return false;
    }
    if (type === 'SUGGESTION' && (!definition.trim() || !sources.trim())) return false;
    return true;
  }, [word, type, reason, definition, sources]);

  const checkWordStatus = async (wordToCheck: string) => {
    if (!wordToCheck || wordToCheck.length < 2 || type === 'REPORT') {
      setWordStatus('IDLE');
      return;
    }

    setIsCheckingWord(true);
    try {
      const res = await fetchWithId(`/word-checker/${encodeURIComponent(wordToCheck)}/meta`);
      const response = await res.json();
      
      if (response.success && response.data) {
        if (!response.data.exists) {
          setWordStatus('NEW');
        } else if (!response.data.meta) {
          setWordStatus('EXISTS_NO_DEF');
        } else {
          setWordStatus('EXISTS_WITH_DEF');
        }
      }
    } catch (err) {
      console.error('Word check failed:', err);
    } finally {
      setIsCheckingWord(false);
    }
  };

  // Debounce word check
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (word && type === 'SUGGESTION') {
        checkWordStatus(word);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [word, type]);

  React.useEffect(() => {
    if (isOpen) {
      setType(initialType);
      setWord(initialWord || '');
      setReason('');
      setDefinition('');
      setSources('');
      setComment('');
      setIsSuccess(false);
      setError(null);
      setWordStatus('IDLE');
    }
  }, [isOpen, initialWord, initialType]);
  const handleWordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase();
    // Allow only Albanian letters (A-Z + Ç, Ë), apostrophe ('), and hyphen (-)
    // Removes numbers, spaces, and special symbols instantly
    const sanitized = val.replaceAll(/[^A-ZÇË'-]/g, '');
    setWord(sanitized);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const payload = {
      type,
      word,
      definition: definition.trim() || undefined,
      sources: sources.trim() || undefined,
      reason: type === 'REPORT' ? reason : undefined,
      comment: comment.trim() || undefined,
      wordId
    };

    try {
      const response = await fetchWithId('/feedback', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Dërgimi dështoi.');

      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        setTimeout(() => setIsSuccess(false), 300);
      }, 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-lg bg-card doodle-border p-6 md:p-10 shadow-doodle-xl overflow-hidden"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full transition-colors boing"
        >
          <X className="w-6 h-6 text-slate-400" />
        </button>

        <div className="space-y-6">
          <div className="space-y-1">
            <div className="text-pink-500 font-black uppercase tracking-widest text-[10px] md:text-xs handwritten flex items-center gap-2">
              {type === 'REPORT' ? <AlertCircle className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
              {type === 'REPORT' ? 'Raporto Fjalën' : 'Sugjero një fjalë të re'}
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-foreground handwritten">
              {type === 'REPORT' ? 'Përmirësimi i fjalorit' : 'Shto në fjalor'}
            </h2>
          </div>

          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-12 flex flex-col items-center justify-center text-center space-y-4"
            >
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-500">
                <CheckCircle2 className="w-12 h-12" />
              </div>
              <div className="space-y-2">
                <p className="font-black handwritten text-xl text-emerald-600 dark:text-emerald-400 uppercase">Faleminderit!</p>
                <p className="text-slate-500 font-bold handwritten">Sugjerimi juaj u dërgua me sukses.</p>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Word Field */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between px-1">
                  <label className="text-[10px] uppercase font-black text-slate-400 handwritten">Fjala *</label>
                  {isCheckingWord ? (
                    <Loader2 className="w-3 h-3 animate-spin text-pink-400" />
                  ) : (
                    wordStatus === 'NEW' && word.length >= 2 && (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      </motion.div>
                    )
                  )}
                </div>
                {type === 'REPORT' ? (
                  <div className="relative py-6 bg-muted/30 doodle-border-soft flex items-center justify-center overflow-hidden">
                    <motion.div 
                      key={word}
                      initial="hidden" animate="visible"
                      variants={{ hidden: { opacity: 1 }, visible: { transition: { staggerChildren: 0.05, delayChildren: 0.2 } } }}
                      className="flex flex-wrap justify-center items-center tracking-tight gap-y-2 w-full px-4"
                    >
                      {(word || "FJALA").toUpperCase().split('').map((char, index) => (
                        <motion.span
                          key={index}
                          variants={{ hidden: { opacity: 0, scale: 0.5, y: 10 }, visible: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 15 } } }}
                          className={cn(
                            "relative font-black text-pink-500 handwritten",
                            word.length > 15 ? "text-2xl md:text-3xl" : word.length > 10 ? "text-3xl md:text-4xl" : "text-4xl md:text-5xl"
                          )}
                        >
                          {char}
                        </motion.span>
                      ))}
                      <motion.div animate={{ opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} className={cn("bg-pink-400 ml-1 rounded-full", word.length > 15 ? "w-0.5 h-6" : "w-1 h-10")} />
                    </motion.div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <input
                      type="text" 
                      required 
                      value={word}
                      onChange={handleWordChange}
                      className="w-full p-4 doodle-border bg-muted/20 font-black handwritten text-2xl text-pink-500 focus:bg-card transition-all placeholder:text-pink-500/20 outline-none"
                      placeholder="SHKRUAJ FJALËN..."
                    />
                    
                    {/* Word Status Info Banner */}
                    <AnimatePresence mode="wait">
                      {wordStatus !== 'IDLE' && wordStatus !== 'NEW' && word.length >= 2 && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className={cn(
                            "p-3 doodle-border-soft text-[10px] md:text-xs font-bold handwritten flex flex-col gap-2",
                            wordStatus === 'EXISTS_NO_DEF' ? "bg-amber-50 text-amber-600 border-amber-200" :
                            "bg-sky-50 text-sky-600 border-sky-200"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            <span>
                              {wordStatus === 'EXISTS_NO_DEF' ? "Këtë fjalë e kemi, por na mungon kuptimi! Na ndihmo ta plotësojmë." :
                               "Kjo fjalë ekziston dhe e kemi kuptimin e saj."}
                            </span>
                          </div>
                          
                          <button
                            type="button"
                            onClick={() => setType('REPORT')}
                            className="text-pink-500 underline text-left hover:text-pink-600 transition-colors"
                          >
                            Dëshiron të raportosh ndonjë gabim për këtë fjalë?
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {type === 'REPORT' && (
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black text-slate-400 handwritten px-1">Arsyeja *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {REPORT_REASONS.map((r) => (
                      <button
                        key={r.id} type="button" onClick={() => setReason(r.id)}
                        className={cn(
                          "p-2.5 text-xs font-black handwritten doodle-border-soft transition-all text-left uppercase tracking-tight",
                          reason === r.id 
                            ? "bg-rose-500 text-white border-rose-600 shadow-doodle-sm" 
                            : "bg-muted/20 text-slate-500 hover:bg-muted/40"
                        )}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Conditional Definition Field for REPORT mode (Missing/Wrong Def) */}
              {type === 'REPORT' && (reason === 'MISSING_DEFINITION' || reason === 'WRONG_DEFINITION') && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="text-[10px] uppercase font-black text-slate-400 handwritten px-1">Kuptimi i duhur *</label>
                  <textarea
                    required
                    value={definition}
                    onChange={(e) => setDefinition(e.target.value)}
                    rows={3}
                    className="w-full p-3 doodle-border-soft bg-muted/20 font-bold handwritten text-base text-sky-500 outline-none resize-none focus:bg-card transition-all placeholder:text-sky-500/20"
                    placeholder="Shkruani këtu kuptimin e saktë..."
                  />
                </div>
              )}

              {type === 'SUGGESTION' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black text-slate-400 handwritten px-1">Kuptimi / Definicioni *</label>
                    <textarea
                      required value={definition} onChange={(e) => setDefinition(e.target.value)}
                      rows={3} className="w-full p-3 doodle-border-soft bg-muted/20 font-bold handwritten text-base text-sky-500 outline-none resize-none focus:bg-card transition-all placeholder:text-sky-500/20"
                      placeholder="Shpjegoni fjalën..."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black text-slate-400 handwritten px-1 flex items-center gap-1">
                      <LinkIcon className="w-3 h-3 text-amber-500" /> Burimi *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {SUGGESTION_SOURCES.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setSources(s.label)}
                          className={cn(
                            "p-2 text-[10px] font-bold handwritten doodle-border-soft transition-all text-center uppercase tracking-tighter",
                            sources === s.label 
                              ? "bg-amber-400 text-white border-amber-500 shadow-doodle-sm" 
                              : "bg-muted/20 text-slate-500 hover:bg-muted/40"
                          )}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-black text-slate-400 handwritten px-1 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3 text-emerald-500" /> Komente shtesë
                </label>
                <textarea
                  value={comment} onChange={(e) => setComment(e.target.value)}
                  rows={2} className="w-full p-3 doodle-border-soft bg-muted/20 font-bold handwritten text-sm text-emerald-600 outline-none resize-none focus:bg-card transition-all placeholder:text-emerald-600/20"
                  placeholder="Na jepni më shumë detaje..."
                />
              </div>

              {error && (
                <div className="p-3 bg-rose-50 dark:bg-rose-900/20 border-2 border-rose-200 dark:border-rose-800 text-rose-500 text-xs font-black handwritten rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit" 
                disabled={isSubmitting || !isValid}
                className={cn(
                  "w-full bg-pink-400 text-white py-4 md:py-5 doodle-border shadow-doodle md:shadow-doodle-lg font-black text-xl md:text-2xl transition-all flex items-center justify-center gap-3",
                  (isSubmitting || !isValid) 
                    ? "opacity-40 cursor-not-allowed grayscale pointer-events-none" 
                    : "hover:translate-x-[-2px] hover:translate-y-[-2px] active:scale-95 boing shadow-rose-200"
                )}
              >
                {isSubmitting ? (
                  <Loader2 className="w-7 h-7 animate-spin" />
                ) : (
                  <>
                    <Send className="w-6 h-6" />
                    DËRGO TANI
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};
